import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, Badge, ListGroup } from "react-bootstrap";
import { FiTrash2 } from "react-icons/fi";
import {
  Calendar,
  Refresh2,
  MoneyRecive,
  MoneySend,
  Warning2,
  TickCircle,
  Clock,
  ArrowDown,
  ArrowUp,
} from "iconsax-react";
import api, { parseWIB } from "../services/api";

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [currentShift, setCurrentShift] = useState(null);
  const [isStaleShift, setIsStaleShift] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [closeSummary, setCloseSummary] = useState(null);

  // Summary panel state
  const [viewMode, setViewMode] = useState("current"); // 'current' | 'history'
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [historyShifts, setHistoryShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(null);

  // Cash flow modal state
  const [showCashFlowModal, setShowCashFlowModal] = useState(false);
  const [cashFlowType, setCashFlowType] = useState("in");
  const [cashFlowName, setCashFlowName] = useState("");
  const [cashFlowAmount, setCashFlowAmount] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (viewMode === "current" && currentShift) {
      loadSummary(currentShift.id);
    } else if (viewMode === "current") {
      setSummary(null);
    }
  }, [viewMode, currentShift]);

  useEffect(() => {
    if (viewMode === "history" && selectedDate) {
      loadHistoryShifts();
    }
  }, [viewMode, selectedDate]);

  useEffect(() => {
    if (selectedShiftId) {
      loadSummary(selectedShiftId);
    }
  }, [selectedShiftId]);

  const loadData = async () => {
    try {
      const [shiftRes, currentRes] = await Promise.all([
        api.get("/shifts"),
        api.get("/shifts/current"),
      ]);
      setShifts(shiftRes.data);
      if (currentRes.data.active) {
        setCurrentShift(currentRes.data.shift);
        setIsStaleShift(!currentRes.data.is_today);
        setSelectedShiftId(currentRes.data.shift.id);
      } else {
        setCurrentShift(null);
        setIsStaleShift(false);
        setSummary(null);
      }
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const loadSummary = async (shiftId) => {
    setSummaryLoading(true);
    try {
      const res = await api.get(`/shifts/${shiftId}/summary`);
      setSummary(res.data);
    } catch (error) {
      toast.error("Gagal memuat summary shift");
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadHistoryShifts = async (keepSelection = false) => {
    try {
      const res = await api.get("/shifts/history", {
        params: { date: selectedDate },
      });
      setHistoryShifts(res.data);
      if (res.data.length > 0) {
        const ids = res.data.map((s) => s.id);
        if (!keepSelection || !ids.includes(selectedShiftId)) {
          setSelectedShiftId(res.data[0].id);
        }
      } else {
        setSelectedShiftId(null);
        setSummary(null);
      }
    } catch (error) {
      toast.error("Gagal memuat history shift");
    }
  };

  const handleOpenShift = async () => {
    try {
      const res = await api.post("/shifts/open", {
        opening_cash: Number(openingCash) || 0,
      });
      if (res.data.already_open) {
        toast.info(res.data.message);
      } else {
        toast.success("Shift berhasil dibuka");
      }
      setShowOpenModal(false);
      setOpeningCash("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal membuka shift");
    }
  };

  const handleCloseShift = async () => {
    try {
      const res = await api.post("/shifts/close", {
        closing_cash: Number(closingCash) || 0,
        notes: closeNotes,
      });
      setCloseSummary(res.data.summary);
      toast.success("Shift berhasil ditutup");
      setClosingCash("");
      setCloseNotes("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menutup shift");
    }
  };

  const handleAddCashFlow = (type) => {
    setCashFlowType(type);
    setCashFlowName("");
    setCashFlowAmount("");
    setShowCashFlowModal(true);
  };

  const handleSubmitCashFlow = async () => {
    if (!cashFlowName || !cashFlowAmount) {
      toast.error("Nama dan jumlah harus diisi");
      return;
    }
    try {
      await api.post("/shifts/cash-flow", {
        shift_id: currentShift.id,
        type: cashFlowType,
        name: cashFlowName,
        amount: Number(cashFlowAmount),
      });
      setShowCashFlowModal(false);
      loadSummary(currentShift.id);
      toast.success("Cash flow ditambahkan");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Gagal menambahkan cash flow",
      );
    }
  };

  const handleDeleteCashFlow = async (id) => {
    if (!window.confirm("Hapus cash flow ini?")) return;
    try {
      await api.delete(`/shifts/cash-flow/${id}`);
      loadSummary(currentShift.id);
      toast.success("Cash flow dihapus");
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus cash flow");
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (
      !window.confirm(
        "Hapus shift ini beserta semua cash flow-nya? Transaksi tidak dihapus.",
      )
    )
      return;
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success("Shift berhasil dihapus");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus shift");
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = parseWIB(d);
    const date = dt.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const time = dt.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    return `${date}, ${time}`;
  };
  const isCurrentOpen =
    viewMode === "current" && currentShift?.status === "open";

  const totalOmzet =
    summary?.payments?.reduce((s, p) => s + Number(p.total), 0) || 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Clock size={22} color="#3b82f6" variant="Bold" />
          <h5 className="fw-bold mb-0">Manajemen Shift</h5>
          {currentShift && (
            <span
              className="badge rounded-pill ms-1"
              style={{
                background:
                  currentShift.status === "open" ? "#d1fae5" : "#f1f5f9",
                color: currentShift.status === "open" ? "#065f46" : "#475569",
                fontSize: "0.72rem",
              }}
            >
              {currentShift.status === "open" ? (
                <>
                  <TickCircle size={11} color="#065f46" /> Buka
                </>
              ) : (
                "Tutup"
              )}
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          {currentShift ? (
            <button
              className="btn btn-danger d-flex align-items-center gap-1"
              onClick={() => setShowCloseModal(true)}
            >
              <TickCircle size={16} />
              Tutup Shift
            </button>
          ) : (
            <button
              className="btn btn-success d-flex align-items-center gap-1"
              onClick={() => setShowOpenModal(true)}
            >
              <MoneyRecive size={16} />
              Buka Shift Baru
            </button>
          )}
        </div>
      </div>

      {/* Stale shift warning */}
      {currentShift && isStaleShift && (
        <div className="alert alert-warning d-flex align-items-start gap-2 mb-3">
          <Warning2
            size={22}
            color="#d97706"
            variant="Bold"
            className="flex-shrink-0 mt-1"
          />
          <div>
            <strong>
              Shift tanggal{" "}
              {parseWIB(currentShift.start_time).toLocaleDateString("id-ID", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}{" "}
              belum ditutup.
            </strong>
            <div className="small mt-1">
              Kasir dapat melanjutkan shift ini atau menutupnya untuk membuka
              shift baru.
            </div>
            <button
              className="btn btn-warning btn-sm mt-2"
              onClick={() => setShowCloseModal(true)}
            >
              Tutup Shift Sekarang
            </button>
          </div>
        </div>
      )}

      {/* Tab: Current / History */}
      <div className="d-flex gap-2 mb-3">
        <button
          className={`btn btn-sm ${viewMode === "current" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setViewMode("current")}
        >
          Shift Aktif
        </button>
        <button
          className={`btn btn-sm d-flex align-items-center gap-1 ${
            viewMode === "history" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setViewMode("history")}
        >
          <Calendar size={14} />
          History
        </button>
        {summary && (
          <button
            className="btn btn-sm btn-outline-secondary ms-auto d-flex align-items-center gap-1"
            onClick={() => selectedShiftId && loadSummary(selectedShiftId)}
            title="Refresh"
          >
            <Refresh2 size={14} />
          </button>
        )}
      </div>

      {/* History: date + shift selector */}
      {viewMode === "history" && (
        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          {historyShifts.length > 0 && (
            <div className="col-md-6">
              <Form.Select
                value={selectedShiftId || ""}
                onChange={(e) => setSelectedShiftId(Number(e.target.value))}
              >
                {historyShifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.user_name} — {fmtDt(s.start_time)}
                    {s.end_time
                      ? ` s/d ${parseWIB(s.end_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}`
                      : " (Masih Buka)"}
                  </option>
                ))}
              </Form.Select>
            </div>
          )}
          {historyShifts.length === 0 && selectedDate && (
            <div className="col-12">
              <div className="alert alert-info py-2 mb-0">
                Tidak ada shift di tanggal ini
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Panel */}
      {summaryLoading && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" />
        </div>
      )}

      {!summaryLoading && summary && (
        <div className="row g-3 mb-4">
          {/* Kas Laci */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Uang di Laci</h6>
                  <Badge
                    bg={
                      summary.shift.status === "open" ? "success" : "secondary"
                    }
                  >
                    {summary.shift.status === "open" ? "Buka" : "Tutup"}
                  </Badge>
                </div>
                <div className="bg-primary text-white rounded p-3 text-center mb-3">
                  <small>
                    {summary.shift.status === "closed"
                      ? "Total Akhir"
                      : "Perkiraan"}
                  </small>
                  <div className="fs-4 fw-bold">
                    {fmt(summary.shift.expected_cash)}
                  </div>
                </div>
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted">Kasir</td>
                      <td className="text-end fw-bold">
                        {summary.shift.user_name}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Kas Awal</td>
                      <td className="text-end">
                        {fmt(summary.shift.opening_cash)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Penjualan Tunai</td>
                      <td className="text-end">
                        {fmt(summary.shift.cash_sales)}
                      </td>
                    </tr>
                    <tr className="text-success">
                      <td>(+) Pemasukan</td>
                      <td className="text-end">{fmt(summary.shift.cash_in)}</td>
                    </tr>
                    <tr className="text-danger">
                      <td>(-) Pengeluaran</td>
                      <td className="text-end">
                        {fmt(summary.shift.cash_out)}
                      </td>
                    </tr>
                    {summary.shift.status === "closed" &&
                      summary.shift.closing_cash != null && (
                        <>
                          <tr>
                            <td className="text-muted">Kas Aktual</td>
                            <td className="text-end">
                              {fmt(summary.shift.closing_cash)}
                            </td>
                          </tr>
                          <tr
                            className={
                              Number(summary.shift.closing_cash) -
                                Number(summary.shift.expected_cash) >=
                              0
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            <td>Selisih</td>
                            <td className="text-end fw-bold">
                              {fmt(
                                Number(summary.shift.closing_cash) -
                                  Number(summary.shift.expected_cash),
                              )}
                            </td>
                          </tr>
                        </>
                      )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Rincian Pembayaran */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="mb-3">Rincian Pembayaran</h6>
                {summary.payments.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    Belum ada transaksi
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {summary.payments.map((p, i) => (
                      <ListGroup.Item
                        key={i}
                        className="d-flex justify-content-between px-0"
                      >
                        <span className="text-capitalize">
                          {p.payment_method}
                        </span>
                        <div className="text-end">
                          <div className="fw-bold">{fmt(p.total)}</div>
                          <small className="text-muted">
                            {p.count} transaksi
                          </small>
                        </div>
                      </ListGroup.Item>
                    ))}
                    <ListGroup.Item className="d-flex justify-content-between px-0 bg-light">
                      <span className="fw-bold">Total Omzet</span>
                      <span className="fw-bold text-primary">
                        {fmt(totalOmzet)}
                      </span>
                    </ListGroup.Item>
                  </ListGroup>
                )}
                {summary.unpaid_count > 0 && (
                  <div className="alert alert-warning py-2 mt-2 mb-0 small">
                    {summary.unpaid_count} transaksi belum dibayar
                  </div>
                )}
                <div className="mt-3 pt-2 border-top">
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Total Transaksi</span>
                    <strong>{summary.shift.total_transactions || 0}</strong>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Total Penjualan</span>
                    <strong>{fmt(summary.shift.total_sales)}</strong>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Non-Cash</span>
                    <strong>{fmt(summary.shift.non_cash_sales)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cash Flow */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <h6 className="mb-3">Pemasukan / Pengeluaran</h6>
                {isCurrentOpen && (
                  <div className="d-grid gap-2 mb-3">
                    <button
                      className="btn btn-sm btn-success d-flex align-items-center justify-content-center gap-1"
                      onClick={() => handleAddCashFlow("in")}
                    >
                      <ArrowDown size={14} /> Tambah Pemasukan
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center gap-1"
                      onClick={() => handleAddCashFlow("out")}
                    >
                      <ArrowUp size={14} /> Tambah Pengeluaran
                    </button>
                  </div>
                )}
                <div style={{ maxHeight: 280, overflowY: "auto", flex: 1 }}>
                  {summary.cashFlows.length === 0 ? (
                    <div className="text-center text-muted py-4 small">
                      Belum ada cash flow
                    </div>
                  ) : (
                    <ListGroup variant="flush">
                      {summary.cashFlows.map((cf, i) => (
                        <ListGroup.Item key={i} className="px-0 py-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="small fw-bold">{cf.name}</div>
                              <div className="small text-muted">
                                {fmtDt(cf.created_at)}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <span
                                className={`fw-bold small d-flex align-items-center gap-1 ${cf.type === "in" ? "text-success" : "text-danger"}`}
                              >
                                {cf.type === "in" ? (
                                  <ArrowDown size={11} color="#059669" />
                                ) : (
                                  <ArrowUp size={11} color="#dc2626" />
                                )}
                                {fmt(cf.amount)}
                              </span>
                              {isCurrentOpen && (
                                <button
                                  className="btn btn-link btn-sm p-0 text-danger"
                                  onClick={() => handleDeleteCashFlow(cf.id)}
                                >
                                  <FiTrash2 size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No active shift message */}
      {viewMode === "current" && !currentShift && !summaryLoading && (
        <div className="alert alert-warning mb-3">
          Tidak ada shift aktif. Buka shift baru untuk mulai transaksi.
        </div>
      )}

      {/* Shift History Table */}
      <div className="card">
        <div className="card-header py-2">
          <small className="fw-bold text-muted">RIWAYAT SHIFT</small>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0 table-sm">
            <thead>
              <tr>
                <th>User</th>
                <th>Mulai</th>
                <th>Selesai</th>
                <th className="text-end">Kas Awal</th>
                <th className="text-end">Kas Akhir</th>
                <th className="text-end">Total Penjualan</th>
                <th className="text-center">Transaksi</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => (
                <tr
                  key={s.id}
                  style={{ cursor: "pointer" }}
                  className={selectedShiftId === s.id ? "table-active" : ""}
                  onClick={() => {
                    setSelectedShiftId(s.id);
                  }}
                >
                  <td className="fw-bold">{s.user_name}</td>
                  <td>{fmtDt(s.start_time)}</td>
                  <td>{fmtDt(s.end_time)}</td>
                  <td className="text-end">{fmt(s.opening_cash)}</td>
                  <td className="text-end">
                    {s.closing_cash != null ? fmt(s.closing_cash) : "-"}
                  </td>
                  <td className="text-end fw-bold">{fmt(s.total_sales)}</td>
                  <td className="text-center">{s.total_transactions}</td>
                  <td>
                    <Badge bg={s.status === "open" ? "success" : "secondary"}>
                      {s.status}
                    </Badge>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-link btn-sm p-0 text-danger"
                      title="Hapus shift"
                      onClick={() => handleDeleteShift(s.id)}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Open Shift Modal */}
      <Modal
        show={showOpenModal}
        onHide={() => setShowOpenModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Buka Shift Baru</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Kas Awal</Form.Label>
            <Form.Control
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              placeholder="0"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOpenModal(false)}>
            Batal
          </Button>
          <Button variant="success" onClick={handleOpenShift}>
            Buka Shift
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Close Shift Modal */}
      <Modal
        show={showCloseModal}
        onHide={() => {
          setShowCloseModal(false);
          setCloseSummary(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {closeSummary ? "Rekap Shift" : "Tutup Shift"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!closeSummary ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Kas Akhir (hitung kas fisik)</Form.Label>
                <Form.Control
                  type="number"
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value)}
                  placeholder="0"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Catatan</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                />
              </Form.Group>
            </>
          ) : (
            <table className="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td className="text-muted">Total Transaksi</td>
                  <td className="text-end fw-bold">
                    {closeSummary.total_transactions}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Total Penjualan</td>
                  <td className="text-end fw-bold">
                    {fmt(closeSummary.total_sales)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Penjualan Cash</td>
                  <td className="text-end">{fmt(closeSummary.cash_sales)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Penjualan Non-Cash</td>
                  <td className="text-end">
                    {fmt(closeSummary.non_cash_sales)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Total Layanan</td>
                  <td className="text-end">{closeSummary.total_services}</td>
                </tr>
                <tr>
                  <td className="text-muted">Total Produk Terjual</td>
                  <td className="text-end">{closeSummary.total_products}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kas Awal</td>
                  <td className="text-end">{fmt(closeSummary.opening_cash)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kas Akhir</td>
                  <td className="text-end">{fmt(closeSummary.closing_cash)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!closeSummary ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setShowCloseModal(false)}
              >
                Batal
              </Button>
              <Button variant="danger" onClick={handleCloseShift}>
                Tutup Shift
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                setShowCloseModal(false);
                setCloseSummary(null);
              }}
            >
              OK
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Cash Flow Modal */}
      <Modal
        show={showCashFlowModal}
        onHide={() => setShowCashFlowModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {cashFlowType === "in"
              ? "+ Tambah Pemasukan"
              : "- Tambah Pengeluaran"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Keterangan</Form.Label>
            <Form.Control
              placeholder="Misal: Beli sabun cuci"
              value={cashFlowName}
              onChange={(e) => setCashFlowName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Jumlah (Rp)</Form.Label>
            <Form.Control
              type="number"
              placeholder="0"
              value={cashFlowAmount}
              onChange={(e) => setCashFlowAmount(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCashFlowModal(false)}
          >
            Batal
          </Button>
          <Button
            variant={cashFlowType === "in" ? "success" : "warning"}
            onClick={handleSubmitCashFlow}
          >
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Shifts;
